import { useSentry } from '@/hooks/useSentry';
import { InputRules, SelectOption } from '@/types/inputs';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

type SVAutocomplete = {
  name: string;
  label: string;
  hideLabel?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  options: SelectOption[];
  rules?: InputRules;
  breadcrumbLabel?: string;
};

export default function SVAutocomplete({
  name,
  label,
  hideLabel,
  disabled = false,
  multiple = false,
  options = [],
  rules,
  breadcrumbLabel,
}: SVAutocomplete) {
  const methods = useFormContext();
  const builtRulesObject = buildInputRulesObject(label, rules);
  const { addUserActionBreadcrumb } = useSentry();
  const currentValue = methods.watch(name);
  const mappedValues: string[] = multiple
    ? Array.isArray(currentValue)
      ? currentValue
      : []
    : currentValue
      ? [currentValue]
      : [];

  return (
    <Controller
      control={methods.control}
      name={name}
      rules={builtRulesObject}
      render={({
        field: { value, onChange, onBlur, ref },
        fieldState: { error },
      }) => (
        <Box sx={{ marginBottom: 2 }}>
          <Autocomplete
            value={
              multiple
                ? Array.isArray(value)
                  ? value.map(v => options.find(opt => opt.value === v) || v)
                  : []
                : value
                  ? options.find(opt => opt.value === value) || null
                  : null
            }
            multiple={multiple}
            filterSelectedOptions
            disabled={disabled}
            options={options}
            getOptionLabel={option => {
              if (
                typeof option === 'object' &&
                option !== null &&
                'label' in option
              ) {
                return option.label;
              }
              // If it's a primitive value (like a UUID string), try to find the option
              const foundOption = options.find(opt => opt.value === option);
              return foundOption ? foundOption.label : String(option);
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={hideLabel ? '' : label}
                variant="outlined"
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
            renderOption={(props, option) => {
              const { label, value } = option;
              const isSelected = mappedValues.includes(value);
              if (isSelected) {
                return null;
              }

              return (
                <Typography {...props} key={`option${value}`}>
                  {label}
                </Typography>
              );
            }}
            onChange={(_e, data) => {
              if (breadcrumbLabel) {
                addUserActionBreadcrumb(
                  `Selected from ${breadcrumbLabel}`,
                  'ui.select',
                );
              }

              // Extract just the value field for single selection, or the value array for multiple selection
              const valueToStore = multiple
                ? data?.map((item: SelectOption) => item.value) || []
                : data?.value || null;

              onChange(valueToStore);
            }}
            onBlur={onBlur}
            ref={ref}
          />
        </Box>
      )}
    />
  );
}
